import React, { useState } from "react";
import html2pdf from "html2pdf.js";
import { toast } from "react-toastify";
import { DownloadExcelsheet, DownloadFormHtmlToPdf, HrMailAPI } from "../api";

var PDF_SOURCES = [
  { id: "pdf-content-sdf", key: "sdf", name: "Self Declaration Form" },
  { id: "pdf-content1", key: "nda", name: "NDA Form" },
  { id: "pdf-content2", key: "epf", name: "EPF Form" },
  { id: "pdf-content3", key: "formf", name: "FormF" },
  { id: "pdf-content4", key: "formq", name: "FormQ" },
  { id: "pdf-content5", key: "form11", name: "Form11" },
];

var TOAST_OPTS = { hideProgressBar: true, closeOnClick: true };

function waitForPdfContentRender() {
  return new Promise(function (resolve) {
    var scheduleFrame =
      window.requestAnimationFrame ||
      function (callback) {
        return setTimeout(callback, 0);
      };
    scheduleFrame(function () {
      scheduleFrame(resolve);
    });
  });
}

async function generatePDF() {
  for (var index = 0; index < PDF_SOURCES.length; index++) {
    var source = PDF_SOURCES[index];
    var element = document.getElementById(source.id);
    if (!element || element.innerHTML === "") {
      throw new Error(source.name + " content is not ready");
    }
    await html2pdf()
      .from(element)
      .set({
        margin: 20,
        filename: source.name + ".pdf",
        pagebreak: { mode: "avoid-all" },
        image: { type: "jpeg", quality: 0.98 },
        // Scale above 2 can exceed the browser canvas limit on long forms.
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: {
          unit: "pt",
          format: "a4",
          orientation: "portrait",
          compress: true,
        },
      })
      .save();
  }
}

async function downloadExcel(id) {
  try {
    var response = await DownloadExcelsheet.getExcelsheet(id);
    var excelBlob = response && response.data ? response.data : null;
    if (!excelBlob || !(excelBlob instanceof Blob)) {
      console.log("Excel download payload:", response);
      throw new Error("Excel download failed: invalid file response");
    }
    var blobUrl = window.URL.createObjectURL(excelBlob);
    var link = document.createElement("a");
    link.href = blobUrl;
    link.download = "NewJoinee.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(function () {
      window.URL.revokeObjectURL(blobUrl);
    }, 1000);
    toast.success("Downloaded All Forms", TOAST_OPTS);
  } catch (error) {
    console.error(error);
    toast.error("Unable to download New Joinee Excel", TOAST_OPTS);
  }
}

/**
 * "Download" (6 form PDFs + Excel) and "Notify Email" for one joinee.
 * Render `pdfHost` once in the page; html2pdf reads the forms from it.
 */
function useJoineeDownloads() {
  var [notifying, setNotifying] = useState(false);
  var [downloading, setDownloading] = useState(false);
  var [showPages, setShowPages] = useState(true);
  var [htmlFormData, setHtmlFormData] = useState({
    nda: "",
    sdf: "",
    formf: "",
    epf: "",
    formq: "",
    form11: "",
  });

  function notify(profileId) {
    if (!profileId) return;
    setNotifying(true);
    HrMailAPI.gethrMail(profileId)
      .then(function (res) {
        if (res.success) {
          toast.success("Email Sent Successfully", TOAST_OPTS);
        } else {
          toast.error(res.message || "Unable to send email", TOAST_OPTS);
        }
      })
      .catch(function (err) {
        console.log(err);
        toast.error("Unable to send email", TOAST_OPTS);
      })
      .finally(function () {
        setNotifying(false);
      });
  }

  async function download(profileId) {
    try {
      if (!profileId) {
        toast.error("Please select a New Joinee first", TOAST_OPTS);
        return;
      }
      setDownloading(true);
      var res = await DownloadFormHtmlToPdf.getform(profileId, "AllHtmlData");
      if (res.success) {
        setHtmlFormData({
          nda: res.data.NdaFormHtmlData,
          sdf: res.data.SdfFormHtmlData,
          formf: res.data.FormFFormHtmlData,
          epf: res.data.EpfFormHtmlData,
          formq: res.data.FormQFormHtmlData,
          form11: res.data.Form11FormHtmlData,
        });
        setShowPages(false);
        await waitForPdfContentRender();
        await generatePDF();
        await downloadExcel(profileId);
      }
    } catch (error) {
      console.log(error);
      toast.error("Unable to download all forms", TOAST_OPTS);
    } finally {
      setDownloading(false);
    }
  }

  var pdfHost = (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        left: "-10000px",
        top: 0,
        width: "794px",
        opacity: showPages ? 1 : 0,
        pointerEvents: "none",
      }}
    >
      {PDF_SOURCES.map(function (source) {
        return (
          <div
            key={source.id}
            id={source.id}
            dangerouslySetInnerHTML={{ __html: htmlFormData[source.key] }}
          />
        );
      })}
    </div>
  );

  return {
    notifying: notifying,
    downloading: downloading,
    notify: notify,
    download: download,
    pdfHost: pdfHost,
  };
}

export default useJoineeDownloads;
